import { Bar, BarChart, LabelList, Pie, PieChart, XAxis } from "recharts";
import { useEffect, useState } from "react";
import { AppLog } from "services";
import { useAppSelector } from "store/store";
import { RequestApi, RouteApi, StatusMsg } from "utils";
import { ChartConfig, ChartContainer, ChartLegend, ChartLegendContent, ChartTooltip, ChartTooltipContent } from "components/ui/chart";

import BusinessSuiteContentLayout from "layout/business-suite-content/BusinessSuiteContentLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "components/ui/card";
import { useLocation } from "react-router-dom";
import DatePicker from "components/app/function/DatePicker";
import moment from "moment";
import ImageUrl from "components/app/image-url/ImageUrl";
import { Service } from "utils/services";
import { CircleAlert } from "lucide-react";

export default function Dashboard() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const shopId = searchParams.get("id");
  const { token } = useAppSelector((state) => state.auth);
  const [searchDate, setSearchDate] = useState("");

  const [dashboardDatas, setDashboardDatas] = useState<any>(null);
  const salesData = dashboardDatas?.sales?.[0] || {};

  const reversedSales = Object.keys(salesData) // Extract keys
    .filter((key) => key.startsWith("sales_amount_")) // Get only sales_amount keys
    .map((key) => salesData[key]) // Extract values
    .reverse(); // Reverse the sales amounts

  const barChartConfig = {
    running: {
      label: "Sales",
      color: "hsl(var(--chart-1))",
    },
  } satisfies ChartConfig;

  const barChartData =
    [...(dashboardDatas?.weekdays || [])].map((date: any, index: number) => ({
      date,
      running: Number(reversedSales[index]) || 0, // Use reversed sales data
    })) || [];

  const pieChartConfig: Record<string, { label: string; color: string }> = {};

  dashboardDatas?.sales_groups?.forEach((item: any, index: number) => {
    const key = item.local_description.toLowerCase();

    // Find a matching key in pieChartConfig
    const matchingKey = Object.keys(pieChartConfig).find((k) => {
      const label = pieChartConfig[k]?.label?.toLowerCase().replace(/[ ,]+/g, "_");
      const description = item.local_description.toLowerCase().replace(/[ ,]+/g, "_");

      return label === description;
    });

    pieChartConfig[key] = {
      label: item.local_description,
      color: matchingKey ? pieChartConfig[matchingKey]?.color ?? `hsl(var(--chart-${index + 1}))` : `hsl(var(--chart-${index + 1}))`,
    };
  });

  const pieChartData =
    dashboardDatas?.sales_groups?.map((item: any) => ({
      name: item.local_description,
      fill: pieChartConfig[item.local_description.toLowerCase()]?.color || `var(--color-other)`,
      sales: parseFloat(item.sales_amount),
    })) || [];
  const [errorStatus, setErrorStatus] = useState({
    title: "",
    message: "",
  });
  const [warning, setWarning] = useState(false);
  const initGetShopDashboard = async () => {
    try {
      let url: string = "";
      url = RouteApi.getShopDashboard;
      url += `token=${token}`;

      const data = {
        shop_id: shopId,
        search_date: searchDate,
      };

      const response = await RequestApi(url, { data });

      if (response.status === "success") {
        setDashboardDatas(response.records);
      } else {
        setErrorStatus({
          title: response.status,
          message: response.msg,
        });
        setWarning(true);
        AppLog.show({ message: response.msg, status: StatusMsg.getType(response.status) });
      }
    } catch (error) {
      AppLog.show({
        message: `[initGetShopDashboard]`,
        status: StatusMsg.error,
        error,
      });
    }
  };

  const formatNumber = (value: number) => {
    if (value >= 1000) {
      return (value / 1000).toFixed(1) + "K";
    }
    return value?.toFixed(2); // Keep two decimal places for smaller numbers
  };

  const groupedProducts = dashboardDatas?.sales_items?.reduce((acc: any, item: any) => {
    const groupName = item.group_local_description || "Unknown Group";
    if (!acc[groupName]) {
      acc[groupName] = [];
    }
    acc[groupName].push(item);
    return acc;
  }, {});

  if (groupedProducts) {
    Object.keys(groupedProducts).forEach((groupName) => {
      groupedProducts[groupName].sort((a: any, b: any) => parseFloat(b.sales_amount) - parseFloat(a.sales_amount));
    });
  }

  useEffect(() => {
    if (searchDate === "") {
      initGetShopDashboard();
    } else {
      initGetShopDashboard();
    }
  }, [searchDate]);

  // console.log("pieChartConfig", pieChartConfig);
  // console.log("pieChartData", pieChartData);

  return (
    <BusinessSuiteContentLayout>
      <div className="min-h-[calc(100vh-120px)] space-y-4 mb-14">
        <div className="flex justify-between">
          <div>
            <h1 className="font-semibold lg:text-lg md:text-md sm:text-sm">Dashboard</h1>
            <div className="space-y-2 text-muted-foreground lg:text-md md:text-sm text-[13px]">
              <div>
                <span className="font-semibold">{moment(dashboardDatas?.starting_date).format("ddd, DD MMM-YYYY")}</span>
                &nbsp;
                <span className="font-semibold">to {moment(dashboardDatas?.ending_date).format("ddd, DD MMM-YYYY")}</span>
              </div>
            </div>
          </div>
          {warning ? (
            ""
          ) : (
            <div className="w-40 md:w-60 lg:w-72">
              <DatePicker
                onChange={(value) => {
                  setSearchDate(value);
                }}
                value=""
              />
            </div>
          )}
        </div>
        {warning ? (
          <div className="flex flex-col gap-2 items-center justify-center h-[70vh] w-full  ">
            <CircleAlert className="text-yellow-500 mx-auto w-[200px] h-[200px]" />
            <h1
              className={`text-[30px] font-semibold   ${
                errorStatus?.title === "success" || errorStatus?.title === "info" ? "text-green-500" : errorStatus?.title === "warning" ? "text-yellow-500" : "text-red-500"
              }`}
            >
              {errorStatus?.title === "success" ? "Success" : errorStatus?.title === "info" ? "Infomation" : errorStatus?.title === "warning" ? "Warning" : "Error"}
            </h1>
            <span className=" text-muted-foreground text-md pt-3 ">{errorStatus?.message}</span>
          </div>
        ) : (
          <>
            {/* Sale & Last 7 Days */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-2">
              <Card className="p-2">
                <h1 className="font-semibold text-sm text-muted-foreground">Today Sales</h1>
                <span className="text-green-600 font-bold md:text-lg lg:text-2xl">{Service.toDouble(dashboardDatas?.sales?.[0]?.sales_amount_1).toFixed(2)}</span>
              </Card>

              <Card className="p-2">
                <h1 className="font-semibold text-sm text-muted-foreground">Last 7 Days</h1>
                <span className="text-blue-600 font-bold md:text-lg lg:text-2xl">
                  {formatNumber(
                    dashboardDatas?.sales?.reduce((sum: number, item: Record<string, any>) => {
                      const itemValues = Object.values(item)
                        .map((value) => (typeof value === "string" ? parseFloat(value) : 0))
                        .filter((value) => !isNaN(value));

                      return sum + itemValues.reduce((acc, num) => acc + num, 0);
                    }, 0)
                  )}
                </span>
              </Card>

              <Card className="p-2">
                <h1 className="font-semibold text-sm text-muted-foreground">Average Sales</h1>
                <span className="text-red-600 font-bold md:text-lg lg:text-2xl">
                  {Number(
                    Service.toDouble(
                      dashboardDatas?.sales?.reduce((sum: number, item: Record<string, any>) => {
                        const itemValues = Object.values(item)
                          .map((value) => (typeof value === "string" ? parseFloat(value) : 0))
                          .filter((value) => !isNaN(value));

                        return sum + itemValues.reduce((acc, num) => acc + num, 0);
                      }, 0) / 7
                    )
                  ).toFixed(2)}
                </span>
              </Card>
            </div>

            {/* Chart */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
              {/* BarChart */}
              <Card>
                <CardHeader>
                  <CardTitle>Weekly Sales Report</CardTitle>
                  <CardDescription>
                    <div className="font-semibold">
                      <span>From {moment(dashboardDatas?.starting_date).format("ddd, DD MMM-YYYY")}</span>
                      &nbsp;
                      <span>to {moment(dashboardDatas?.ending_date).format("ddd, DD MMM-YYYY")}</span>
                    </div>
                  </CardDescription>
                </CardHeader>
                <CardContent className="">
                  <ChartContainer config={barChartConfig}>
                    <BarChart accessibilityLayer data={barChartData}>
                      <XAxis
                        dataKey="date"
                        tickLine={false}
                        tickMargin={10}
                        axisLine={false}
                        tickFormatter={(value) => {
                          return new Date(value).toLocaleDateString("en-US", {
                            weekday: "short",
                          });
                        }}
                      />
                      <Bar dataKey="running" stackId="a" fill="var(--color-running)" radius={[0, 0, 4, 4]} />
                      <Bar dataKey="swimming" stackId="a" fill="var(--color-swimming)" radius={[4, 4, 0, 0]} />
                      <ChartTooltip content={<ChartTooltipContent />} cursor={false} defaultIndex={1} />
                    </BarChart>
                  </ChartContainer>
                </CardContent>
              </Card>

              {/* PieChart */}
              <Card className="">
                <CardHeader className="">
                  <CardTitle>Sales Summary By Group</CardTitle>
                  <CardDescription className="font-semibold">{moment(dashboardDatas?.ending_date).format("ddd, DD MMM-YYYY")}</CardDescription>
                </CardHeader>
                <CardContent className="flex justify-between items-center space-x-4">
                  <ChartContainer config={pieChartConfig} className="mx-auto aspect-square max-h-[400px] [&_.recharts-text]:fill-background flex-1">
                    <PieChart className="">
                      <ChartTooltip content={<ChartTooltipContent nameKey="sales" hideLabel />} />
                      <Pie data={pieChartData} dataKey="sales">
                        <LabelList dataKey="sales" className="fill-background" stroke="none" fontSize={12} formatter={(value: string) => value} />
                      </Pie>

                      {/* ChartLegend is inside the ChartContainer */}
                      <ChartLegend content={<ChartLegendContent nameKey="name" />} className="-translate-y-2 flex-wrap gap-2 [&>*]:basis-1/4 [&>*]:justify-center" />
                    </PieChart>
                  </ChartContainer>
                </CardContent>
              </Card>
            </div>

            <h1 className="text-2xl font-semibold leading-none tracking-tight mt-4">Sales Summary By Item</h1>
            <div className="font-semibold text-sm text-muted-foreground">
              <span>From {moment(dashboardDatas?.starting_date).format("ddd, DD MMM-YYYY")}</span>
              &nbsp;
              <span>to {moment(dashboardDatas?.ending_date).format("ddd, DD MMM-YYYY")}</span>
            </div>
            {groupedProducts && (
              <>
                {Object.keys(groupedProducts).map((groupName) => (
                  <div className="" key={groupName}>
                    <h3 className="font-bold text-xl mt-4 mb-2">{groupName}</h3>

                    {/* Render products for this group */}
                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-2">
                      {groupedProducts[groupName].map((item: any) => (
                        <>
                          <Card key={item.product_id} className="overflow-hidden p-2 w-full">
                            <div className="flex w-full">
                              <ImageUrl src={item.thumbnail} className="w-20 h-20 rounded-lg" />
                              <div className="w-full pl-2">
                                <p className="font-medium line-clamp-1">{item.local_description}</p>

                                <div className="flex justify-between w-full">
                                  <p className="text-sm font-medium text-muted-foreground w-full pt-1">
                                    {item.sales_quantity} <span>{item.unit_of_measure}</span>
                                  </p>
                                  <p className="font-bold text-destructive text-xl">{item.sales_amount}</p>
                                </div>
                              </div>
                            </div>
                          </Card>
                        </>
                      ))}
                    </div>
                  </div>
                ))}
              </>
            )}
          </>
        )}
      </div>
    </BusinessSuiteContentLayout>
  );
}
